import qs from 'qs'
import Vue from 'vue'
import Slugify from '../utils/slugify'
import validations from './validations'
import parseNumber from '../utils/parse-number'

// TODO: add durations options in plugin
// add combiDiscounts in options
// allProductsSelected Discount

export const state = () => ({
  user: {
    first_name: '',
    last_name: '',
    email: ''
  },
  products: {},
  discounts: [],
  userDiscount: {},
  visibleProducts: [],
  combiProducts: {},
  masterCombi: null,
  masterCombiDiscountName: '',
  combisWithSubreports: [],
  combisDiscounts: {},
  selectedProducts: [],
  durations: [],
  selectedDuration: null,
  selectedGateway: {},
  selectedIssuer: '',
  apiErrors: {},
  queries: {}
})

export const mutations = {
  SET_USER (state, data) {
    state.user = data
  },
  SET_USER_FIELD (state, data) {
    Vue.set(state.user, data.field, data.value)
  },
  SET_PRODUCTS (state, data) {
    state.products = data
  },
  SET_VISIBLE_PRODUCTS (state, data) {
    state.visibleProducts = data
  },
  SET_COMBI_PRODUCTS (state, data) {
    state.combiProducts = data
  },
  SET_COMBIS_DISCOUNTS (state, data) {
    state.combisDiscounts = data
  },
  SET_MASTER_COMBI (state, data) {
    state.masterCombi = data
  },
  SET_MASTER_COMBI_DISCOUNT_NAME (state, data) {
    state.masterCombiDiscountName = data
  },
  SET_COMBIS_WITH_SUBREPORTS (state, data) {
    state.combisWithSubreports = data
  },
  SET_DISCOUNTS (state, data) {
    state.discounts = data
  },
  SET_USER_DISCOUNT (state, data) {
    if (data.value) {
      data.value = parseNumber(data.value)
    }
    state.userDiscount = data
  },
  SET_SELECTED_PRODUCTS (state, productId) {
    const index = state.selectedProducts.indexOf(productId)
    if (index === -1) {
      state.selectedProducts.push(productId)
    } else {
      state.selectedProducts.splice(index, 1)
    }
  },
  SET_SELECTED_COMBI (state, combiId) {
    const index = state.selectedProducts.indexOf(combiId)
    const subreportsInSelected = state.selectedProducts.filter((selectedId) => {
      return state.combiProducts[combiId].includes(selectedId)
    })
    const allSubreportsSelected = subreportsInSelected.length === state.combiProducts[combiId].length
    if (index === -1 && allSubreportsSelected) {
      state.selectedProducts.push(combiId)
    } else if (index !== -1 && !allSubreportsSelected) {
      state.selectedProducts.splice(index, 1)
    }
  },
  TOGGLE_SELECTED_SUBREPORTS (state, {id, value}) {
    const subreports = state.combiProducts[id]
    const productsWithoutReports = state.selectedProducts.filter(id => !subreports.includes(id))
    if (value) {
      state.selectedProducts = productsWithoutReports.concat(subreports)
    } else {
      state.selectedProducts = productsWithoutReports
    }
  },
  SET_SELECTED_PRODUCTS_ARRAY (state, productIds) {
    state.selectedProducts = productIds
  },
  SET_SELECTED_DURATION (state, data) {
    state.selectedDuration = data
  },
  SET_DURATIONS (state, data) {
    state.durations = data
  },
  SET_GATEWAY (state, data) {
    state.selectedGateway = data
  },
  SET_ISSUER (state, data) {
    state.selectedIssuer = data
  },
  SET_API_ERRORS (state, data) {
    state.apiErrors = data
  },
  SET_QUERIES (state, data) {
    state.queries = data
  }
}

export const getters = {
  // api returns an object for the products
  // convert it into an array for easier manipulation
  productsArray: (state) => {
    return Object.values(state.products) || []
  },
  visibleProductsArray: (state, getters) => {
    if (getters.productsArray.length === 0) return []
    if (state.visibleProducts.length === 0) return getters.productsArray
    return state.visibleProducts.map(id => getters.productsArray.find(product => product.id === id) || { id: id, name: 'Error: There is no product with this id'})
  },
  productsArraySorted: (state, getters) => {
    const products = [...getters.productsArray]
    return products.sort((a, b) => {
      if (state.visibleProducts.indexOf(a.id) === -1 && state.visibleProducts.indexOf(b.id) === -1) {
        return (a.id - b.id) // -1
      }
      if (state.visibleProducts.indexOf(b.id) === -1) {
        return -1
      }
      if (state.visibleProducts.indexOf(a.id) === -1) {
        return 1
      }
      return state.visibleProducts.indexOf(a.id) - state.visibleProducts.indexOf(b.id)
    })
  },
  combisWithSubreportsMap: (state, getters) => {
    const map = {}
    state.combisWithSubreports.forEach((combiID) => {
      const products = state.combiProducts[combiID].map((id) => {
        const product = getters.productsArray.find(product => product.id === id)
        return product || false
      })
      map[combiID] = products.filter(product => product)
    })
    return map
  },
  allSubreports: (state, getters) => {
    const subreports = []
    for (let key in getters.combisWithSubreportsMap) {
      subreports.push(getters.combisWithSubreportsMap[key])
    }
    return subreports.flat()
  },
  productsSubscriptionTypes: (state, getters) => {
    return getters.productsArray.map(product => product.subscriptionTypes).flat()
  },
  selectedSubscriptionTypes: (state, getters) => {
    if (state.selectedProducts.length === 0) return []
    const products = getters.productsArraySorted.filter((product) => {
      const combisWithSubreportsSelected = state.selectedProducts.filter(selected => state.combisWithSubreports.includes(selected))
      const productIsInCombiWithSubreports = combisWithSubreportsSelected.length > 0 && combisWithSubreportsSelected.some(combiID => state.combiProducts[combiID].includes(product.id))
      return state.selectedProducts.includes(product.id) && !productIsInCombiWithSubreports
    })
    const subscriptions = products.map(product => product.subscriptionTypes).flat()
    return subscriptions.filter(subscription => subscription.period_value === state.selectedDuration)
  },
  getSelectedProducts: (state, getters) => {
    return getters.selectedSubscriptionTypes.map(sub => sub.series_id || sub.series_group_id)
  },
  gateways: (state, getters) => {
    if (getters.selectedSubscriptionTypes.length === 0) return []
    return getters.selectedSubscriptionTypes[0].gateways
  },
  selectedCombis: (state) => {
    const ids = []
    for (let key in state.combiProducts) {
      const combiInSelected = state.selectedProducts.filter(id => state.combiProducts[key].includes(id))
      if (combiInSelected.length === state.combiProducts[key].length) {
        ids.push(parseInt(key))
      }
    }
    return ids
  },
  selectedhasCombi: (state, getters) => {
    return getters.selectedCombis.length > 0
  },
  selectedCombiDiscountName: (state, getters) => {
    if (!getters.selectedhasCombi) return false
    // user has selected at least one combi
    // apply first combi discoun or if all products are selected apply master combi discount
    let discountName =  state.masterCombiDiscountName // discount for selecting all products
    const allProductsSelected = getters.selectedProductsWithoutSubreports.length === getters.visibleProductsArray.length
    if (getters.selectedCombisWithSubreports.length > 0 && (!allProductsSelected || !discountName)) {
      // always select the first combi - we do not apply multiple discounts
      const id = getters.selectedCombisWithSubreports[0]
      discountName = state.combisDiscounts[id]
    }
    return discountName
  },
  combiDiscount: (state, getters) => {
    if (!getters.selectedhasCombi) return false
    return getters.parsedDiscounts.find(discount => discount.name === getters.selectedCombiDiscountName && discount.period === state.selectedDuration) || false
  },
  selectedCombisWithSubreports: (state, getters) => {
    return getters.selectedCombis.filter(id => state.combisWithSubreports.includes(id))
  },
  selectedProductsHaveDiscount: (state, getters) => {
    if (Object.keys(state.userDiscount).length > 0) {
      // HOTFIX include master combi to check for discount
      let selectedSubscriptionTypesWithMasterCombi = [...getters.selectedSubscriptionTypes]
      if (getters.selectedCombis.includes(state.masterCombi)) {
        const masterCombiSubscription = getters.productsSubscriptionTypes.find(subscription => subscription.series_group_id === state.masterCombi && subscription.period_value === state.selectedDuration)
        selectedSubscriptionTypesWithMasterCombi = selectedSubscriptionTypesWithMasterCombi.concat(masterCombiSubscription)
      }
      if (state.userDiscount.logic === 'and') {
        return state.userDiscount.subscriptionTypes.every((id) => {
          return selectedSubscriptionTypesWithMasterCombi.map(item => item.id).includes(id)
        })
      }
      return selectedSubscriptionTypesWithMasterCombi.some((subscription) => {
        return state.userDiscount.subscriptionTypes.includes(subscription.id)
      })
    }
    return false
  },
  selectedProductsPercentageDiscount: (state, getters) => {
    if (!getters.selectedProductsHaveDiscount) return 0
    if (state.userDiscount && state.userDiscount.type === 'percentage') {
      const selectedProductsWithDiscount = getters.selectedSubscriptionTypes.filter((subscription) => {
        return state.userDiscount.subscriptionTypes.includes(subscription.id)
      })
      // return selectedProductsWithDiscount
      if (selectedProductsWithDiscount.length > 0) {
        const discountValue = selectedProductsWithDiscount.reduce((acc, curr) => {
          return acc + parseFloat(curr.price.totalPrice * parseFloat(state.userDiscount.value / 100))
        }, 0)
        return !getters.combiDiscount || discountValue > getters.combiDiscount.value ? discountValue : 0
      }
    }
    return 0
  },
  selectedProductsWithoutCombis: (state, getters) => {
    let exclude = getters.selectedCombis
    const combiProducts = getters.selectedCombis.map(id => state.combiProducts[id]).flat()
    exclude = exclude.concat(combiProducts)
    return state.selectedProducts.filter(id => !exclude.includes(id))
  },
  selectedProductsWithoutSubreports: (state, getters) => {
    if (state.selectedProducts.length === 0 || getters.allSubreports.length === 0) return []
    return state.selectedProducts.filter(id => !getters.allSubreports.some(subreport => subreport && subreport.id === id))
  },
  parsedDiscounts: (state) => {
    return state.discounts.map((discount) => {
      const arr = discount.code.split('-')
      discount.name = arr.length === 2 ? arr[0] : arr.splice(0, arr.length - 1).join('-')
      discount.period = parseInt(arr.slice(-1))
      discount.value = parseNumber(discount.value)
      return discount
    })
  },
  discount: (state, getters) => {
    let userDiscount = false
    // user has entered a discount code
    if (getters.selectedProductsHaveDiscount) {
      userDiscount = getters.selectedProductsPercentageDiscount ? { code: state.userDiscount.code, value: getters.selectedProductsPercentageDiscount } : state.userDiscount
    }
    // 0 - user discount if it's bigger than combi discount
    // 1 - combidiscount
    // 2 - user discount (can be false)
    if (userDiscount && userDiscount.value) {
      if (userDiscount.value > getters.combiDiscount.value) {
        return userDiscount
      }
    }
    if (getters.combiDiscount) {
      return getters.combiDiscount
    }
    return userDiscount
  },
  discountValue: (state, getters) => {
    return parseFloat(getters.discount.value) || 0
  },
  userDiscountDurations: (state, getters) => {
    if (Object.keys(state.userDiscount).length === 0) return []
    const discountSubscriptionTypes = state.userDiscount.subscriptionTypes
    const durations = getters.productsSubscriptionTypes.filter(sub => discountSubscriptionTypes.includes(sub.id)).map(sub => sub.period_value)
    return [...new Set(durations)]
  },
  totalPrice: (state, getters) => {
    if (getters.selectedSubscriptionTypes.length === 0) return 0
    return getters.selectedSubscriptionTypes.reduce((acc, curr) => {
      return acc + curr.price.totalPrice
    }, 0)
  },
  totalPriceAfterDiscount: (state, getters) => {
    if (getters.selectedSubscriptionTypes.length === 0) return 0
    return getters.totalPrice - getters.discountValue
  },
  issuer: (state) => {
    if (state.selectedGateway.paymentMethod === 'IDEAL' && state.selectedIssuer) return { issuer: state.selectedIssuer }
    if (state.selectedGateway.issuers && state.selectedGateway.issuers.length > 0) return { issuer: state.selectedGateway.issuers[0].paymentMethod }
    return null
  },
  selectedProductsMonthlyPrices: (state, getters) => {
    let selectedCanbeDiscounted = false
    const periodPrices = { 1: 0, 6: 0, 12: 0, 24: 0}
    const products = getters.productsArraySorted.filter(product => getters.getSelectedProducts.includes(product.id))
    const subscriptionTypes = products.map(product => product.subscriptionTypes).flat()
    subscriptionTypes.forEach((subscription) => {
      periodPrices[subscription.period_value] += parseFloat(subscription.price.periodPrice)
    })

    // we need to include the master combi, only to check if the selected products could potentially be discounted, even if not selected
    const productsWithMasterCombi = getters.selectedCombis.includes(state.masterCombi) ? [...getters.productsArray.filter(product => product.id === state.masterCombi), ...products] : products
    const subscriptionsWithMasterCombi = productsWithMasterCombi.map(product => product.subscriptionTypes).flat()
    selectedCanbeDiscounted = subscriptionsWithMasterCombi.some(sub => state.userDiscount.subscriptionTypes && state.userDiscount.subscriptionTypes.includes(sub.id))

    if (getters.selectedhasCombi) {
      // monkey patch for when the master combi does not have a discountName
      const selectedCombi = getters.selectedCombis.includes(state.masterCombi) && state.masterCombiDiscountName ? getters.selectedCombis[0] : getters.selectedCombis[1]
      const selectedCombiSubscriptions = getters.productsSubscriptionTypes.filter(sub => sub.series_group_id === selectedCombi).map(sub => sub.id)
      const combiDiscount = getters.parsedDiscounts.filter(discount => discount.subscriptionTypes.filter(sub => selectedCombiSubscriptions.includes(sub)).length > 0)
      const excludeUserDiscountDurations = selectedCanbeDiscounted ? combiDiscount.filter((discount => !getters.userDiscountDurations.includes(discount.period))) : combiDiscount
      excludeUserDiscountDurations.forEach((discount) => {
        const period = discount.period
        const periodPrice = periodPrices[period] * period
        const discountValue = parseFloat(discount.value)
        periodPrices[period] = parseFloat(((periodPrice - discountValue) / period))
      })
    }
    // user Discount exists and is/can be applied
    if (selectedCanbeDiscounted) {
      const combiDiscounts = getters.parsedDiscounts
        .filter(discount => discount.name === getters.selectedCombiDiscountName)
      getters.userDiscountDurations.forEach((duration) => {
        const periodPrice = Math.round(periodPrices[duration] * duration)
        const userDiscountValue = state.userDiscount.type === 'percentage' ? (periodPrice * state.userDiscount.value) / 100 : state.userDiscount.value
        const combiDiscountForDuration = combiDiscounts.find(discount => discount.period === duration) || { value: 0}
        const discountValue = userDiscountValue > combiDiscountForDuration.value ? userDiscountValue : combiDiscountForDuration.value
        periodPrices[duration] = parseFloat((periodPrice - discountValue) / duration)
      })
    }
    return periodPrices
  },
  savings: (state, getters) => {
    return {
      12: (Math.abs(getters.selectedProductsMonthlyPrices[12] - getters.selectedProductsMonthlyPrices[6])).toFixed(2),
      24: (Math.abs(getters.selectedProductsMonthlyPrices[24] - getters.selectedProductsMonthlyPrices[6])).toFixed(2)
    }
  }
}

export const actions = {
  updateUrl ({ state, getters }) {
    const prefix = (url) => url.length > 1 ? '&' : ''
    const uniqueSet = new Set([...getters.selectedProductsWithoutSubreports, ...getters.selectedProductsWithoutCombis])
    const selected = [...uniqueSet]
    const query = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    // eslint-disable-next-line no-unused-vars
    const { [state.queries.discount]: discount, [state.queries.report]: report, [state.queries.period]: period, ...rest  } = query
    // build the new url
    let $url = '?'
    // preserve any query other than report, period or discount
    $url += qs.stringify(rest)
    // reports
    const products = getters.productsArraySorted.filter((product) => selected.includes(product.id))
    const slugs = products.map((product) => Slugify(product.name))
    if (slugs.length > 0) {
      $url += `${prefix($url)}${state.queries.report}=${slugs.join(',')}`
    }
    // period
    $url += `${prefix($url)}${state.queries.period}=${state.selectedDuration}`
    // discount
    if (discount) {
      $url += `&${state.queries.discount}=${discount}`
    }
    window.history.replaceState(null, null, $url)
  },
  selectProductsBySlug ({ state, getters, commit }, slugs) {
    const products = getters.productsArray.filter(product => {
      return slugs.includes(Slugify(product.name))
    })
    if (products.length > 0) {
      const productIds = products.map(product => product.id)
      commit('SET_SELECTED_PRODUCTS_ARRAY', productIds)
      const combis = productIds.filter(id => state.combiProducts[id])
      if (combis.length > 0) {
        combis.forEach((combiId) => {
          state.combiProducts[combiId].forEach((subreportId) => {
            commit('SET_SELECTED_PRODUCTS', subreportId)
          })
        })
      }
    }
  },
  async getUser ({ commit }) {
    const { data } = await this.$axios.get('/user')
    if (data) commit('SET_USER', data)
    return data
  },
  async getProducts ({ commit }) {
    const { data } = await this.$axios.get('/products')
    commit('SET_PRODUCTS', data)
    return data
  },
  async getDiscounts ({ commit }) {
    const { data } = await this.$axios.get('/discounts')
    commit('SET_DISCOUNTS', data)
    return data
  },
  async getDiscount ({ commit }, code) {
    const { data } = await this.$axios.get(`/discount?code=${code}`)
    const discount = !Array.isArray(data) ? data : {}
    commit('SET_USER_DISCOUNT', discount)
    return data
  },
  async submitOrder ({ state, commit, getters }) {
    commit('SET_API_ERRORS', {})
    const allProductsSelected = getters.selectedCombis.includes(state.masterCombi)
    let subscriptionTypes = []
    if (allProductsSelected) {
      const masterCombiSubscription = getters.productsSubscriptionTypes.find(subscription => subscription.series_group_id === state.masterCombi && subscription.period_value === state.selectedDuration)
      subscriptionTypes.push(masterCombiSubscription.id)
    } else {
      const combis = getters.selectedCombis.map(id => {
        return getters.productsSubscriptionTypes.find(subscription => subscription.series_group_id === id && subscription.period_value === state.selectedDuration).id
      })
      const subscriptions = getters.selectedProductsWithoutCombis.map(productId => getters.productsSubscriptionTypes.find(subscription => subscription.series_id === productId && subscription.period_value === state.selectedDuration))
      const subscriptionIds = subscriptions.map(subscription => subscription.id)
      subscriptionTypes = combis.concat(subscriptionIds)
    }

    const order = {
      ...state.user,
      subscription_types: subscriptionTypes,
      gateway: state.selectedGateway.paymentMethod,
      ...getters.issuer,
      discount: getters.discount.code || null
    }
    try {
      const { data } = await this.$axios.post('/submit-order', qs.stringify(order))
      return data
    } catch(e) {
      if (e.response && e.response.data) {
        commit('SET_API_ERRORS', e.response.data)
      }
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
  modules: {
    validations: validations
  }
}
